<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { FileText, User } from 'lucide-vue-next';
import { useRouter } from 'vue-router';
import authService from '../api/auth';

const router = useRouter();
const user = ref<any>(null);

const isAuthenticated = computed(() => authService.isAuthenticated());

const userInitials = computed(() => {
  if (!user.value?.firstName || !user.value?.lastName) return '';
  return `${user.value.firstName[0]}${user.value.lastName[0]}`.toUpperCase();
});

const goToLogin = () => {
  router.push('/login');
};

const goToDashboard = () => {
  router.push('/dashboard');
};

const scrollToSection = (sectionId: string) => {
  const targetElement = document.getElementById(sectionId);
  if (!targetElement) return;

  const headerOffset = 80;
  const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - headerOffset;
  const startPosition = window.scrollY;
  const distance = targetPosition - startPosition;
  const duration = 360;
  let startTime: number | null = null;

  const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

  const animate = (currentTime: number) => {
    if (startTime === null) {
      startTime = currentTime;
    }

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);

    window.scrollTo(0, startPosition + distance * easedProgress);

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

onMounted(async () => {
  if (!isAuthenticated.value) return;
  try {
    user.value = await authService.getCurrentUser();
  } catch {
    user.value = null;
  }
});
</script>

<template>
  <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-[auto_1fr_auto] h-16 items-center gap-4">
        <div class="flex items-center gap-2">
          <FileText class="h-8 w-8 text-indigo-600" />
          <span class="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
            CV-Tailor AI
          </span>
        </div>

        <div class="flex justify-center">
          <div class="flex items-center gap-8">
            <a href="#features" class="text-slate-600 hover:text-indigo-600 font-medium transition" @click.prevent="scrollToSection('features')">Technologia</a>
            <a href="#how-it-works" class="text-slate-600 hover:text-indigo-600 font-medium transition" @click.prevent="scrollToSection('how-it-works')">Jak to działa</a>
            <a href="#faq" class="text-slate-600 hover:text-indigo-600 font-medium transition" @click.prevent="scrollToSection('faq')">FAQ</a>
          </div>
        </div>

        <div class="flex justify-end">
          <button
            v-if="isAuthenticated"
            type="button"
            @click="goToDashboard"
            class="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-tr from-indigo-500 to-violet-500 text-white font-bold hover:shadow-lg hover:shadow-indigo-500/30 transition"
            aria-label="Przejdź do dashboardu"
            title="Moje konto"
          >
            <span v-if="userInitials">{{ userInitials }}</span>
            <User v-else class="h-5 w-5" />
          </button>

          <button
            v-else
            type="button"
            @click="goToLogin"
            class="ml-1 bg-slate-900 text-white px-5 py-2 rounded-full font-medium hover:bg-slate-800 transition shadow-lg shadow-indigo-500/20"
          >
            Zaloguj się
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>
